/**
 * CoreHostGuard.tsx — the public acquisition journey is not served under GPS Core.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: Clinics-in-Core (Clinics ADR-1,
 * 2026-09-07).
 *
 * WHY. When GPS Core fronts this build at `<core-host>/clinics/*`, everyone who
 * arrives is a signed-in Core user reaching their OWN records. The acquisition
 * journey — the questionnaire, plan selection, checkout and the free callback
 * form — is the front door for people who are not yet patients, and it stays
 * where it has always lived: the standalone portal (app.clinics), with its
 * marketing measurement, its consent banner and its Webflow entry points. So on
 * the Core-hosted image `/:clinicSlug` and `/:clinicSlug/callback` hand the
 * visitor over to the standalone portal, same path, query and FRAGMENT intact
 * (a `#resume=` plan link minted inside Core must land, not be dropped).
 *
 * A FULL-DOCUMENT `location.replace`, not a router redirect: the destination is
 * another origin. `replace` so the Core URL never sits in history behind the
 * journey — Back from the standalone portal returns the visitor to Core's own
 * surface, not to a page that immediately forwards again.
 *
 * FAIL-SAFE. Standalone builds render the journey untouched (this component is a
 * pass-through unless the build is Core-hosted). A Core-hosted build with no
 * `VITE_PUBLIC_PORTAL_ORIGIN` configured also renders the journey in place — a
 * visible page a person can act on beats a redirect to nowhere.
 */
import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { hostReturnPath, isCoreHosted, publicPortalOrigin } from '../config';
import { PageFrame, Spinner } from '../ui/kit';
import { useAuth } from './AuthContext';
import { homeForRole } from './roleHome';

/**
 * The standalone-portal URL a Core-hosted visitor is handed to, or null when
 * there is nowhere to send them (not Core-hosted, or no origin configured).
 * `pathname` is the ROUTER's (basename-relative) path — '/weightloss', never
 * '/clinics/weightloss' — so the target is the standalone portal's own route.
 */
export function journeyRedirectTarget(
  location: { pathname: string; search: string; hash: string },
  coreHosted: boolean = isCoreHosted(),
  origin: string = publicPortalOrigin(),
): string | null {
  if (!coreHosted || !origin) return null;
  return `${origin}${location.pathname}${location.search}${location.hash}`;
}

/**
 * IndexRedirect — where `/` goes. Standalone: the public journey (`/weightloss`),
 * exactly as it always has. Core-hosted: a signed-in person goes to THEIR OWN
 * portal and a signed-out one goes back to Core (full document) — never to the
 * acquisition journey, which under Core would bounce them straight out to the
 * standalone portal's marketing front door. Every in-app `<Link to="/">` (the
 * DesktopOnly notice's "Back to GPS Clinics", the brand lockup) lands here.
 */
export function IndexRedirect() {
  const { me, status } = useAuth();
  if (!isCoreHosted()) return <Navigate to="/weightloss" replace />;
  if (status === 'loading') {
    return (
      <PageFrame narrow>
        <Spinner label="Checking your session…" />
      </PageFrame>
    );
  }
  if (me) return <Navigate to={homeForRole(me.role)} replace />;
  return <HostReturn />;
}

/** A full-document return to the Core host (the same-origin path hostReturnPath() vouches for). */
function HostReturn() {
  const target = hostReturnPath();
  useEffect(() => {
    window.location.replace(target);
  }, [target]);
  return (
    <PageFrame narrow>
      <Spinner label="Taking you back to Consultations…" />
    </PageFrame>
  );
}

/** Render the public journey — or, on the Core-hosted image, hand over to the standalone portal. */
export function CoreHostGuard({ children }: { children: ReactNode }) {
  const location = useLocation();
  const target = journeyRedirectTarget(location);

  useEffect(() => {
    if (target) window.location.replace(target);
  }, [target]);

  if (target) {
    return (
      <PageFrame narrow>
        <Spinner label="Taking you to the clinic…" />
      </PageFrame>
    );
  }
  return <>{children}</>;
}
